import { ActivatedRoute, Data, Router, RouterLink, Params } from '@angular/router';
import { AddVesselFormComponent } from '@components/vessel/add-form/form.component';
import { AuthService, NotificationService, VesselService } from '@services';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { environment } from '@environment';
import { FormsModule } from '@angular/forms';
import { HttpParams, httpResource } from '@angular/common/http';
import { ListComponent } from '@components/list/list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Permission, VesselEntry, VesselListResponse } from '@models';

@Component({
  selector: 'app-vessel-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    FormsModule,
    ListComponent,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatPaginatorModule,
    RouterLink,
  ],
})

export class VesselListComponent {
  // Router
  route = inject(ActivatedRoute);
  router = inject(Router);

  // environment
  kinko = environment.kinkoHost;
  vesselDataset = environment.vesselDatasetId;

  // Services
  authService = inject(AuthService);
  vesselService = inject(VesselService);
  notificationService = inject(NotificationService);

  // User & Permissions
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  // Page
  defaultPageSize = 15;
  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || this.defaultPageSize);
  search = signal(this.route.snapshot.queryParams['q'] || '');

  count = signal(0);
  vessels = httpResource<VesselListResponse>(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    const httpParams = new HttpParams()
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize())
      .set('count', count)
      .set('order', 'created:desc')
      .set('q', this.search());

    return `${this.kinko}/dataset/${this.vesselDataset}/record/?${httpParams.toString()}`;
  });

  // Dialogbox to add new vessel to the archive.
  dialog = inject(MatDialog);

  constructor() {
    this.route.data.subscribe((data: Data) => {
      this.permissions.set(data['permissions']);
    });

    this.route.queryParams.subscribe((params: Params) => {
      this.search.set(params['q'] as string || '');
      this.page.set(parseInt(params['page']) || 0);
      this.pageSize.set(parseInt(params['pageSize']) || this.defaultPageSize);
    });

    effect(() => {
      if (!this.vessels.hasValue() || !this.vessels.value().count) {
        return;
      }

      this.count.set(this.vessels.value().count);
    });
  };

  onAddVesselClick() {
    const dialogRef = this.dialog.open(AddVesselFormComponent, {
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((name: string) => {
      if (!name) {
        return;
      }

      this.vesselService.addVessel(name).subscribe({
        next: (vessel: VesselEntry) => {
          this.router.navigate([vessel.id, 'edit'], { relativeTo: this.route });
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to add vessel to registry.')
        }
      })
    });
  }

  onSearch(event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: (event.target as HTMLInputElement).value || '',
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  onPage(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        page: event.pageIndex || undefined,
        pageSize: event.pageSize === this.defaultPageSize ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }

}
